import React, { useEffect, useState } from 'react'
import { Card, Spin, Tag, Button, Empty, message, Image, Select } from 'antd'
import { GET } from '../../utils/apis'
import { CarOutlined, ClockCircleOutlined, CreditCardOutlined, FileTextOutlined, QrcodeOutlined, ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons'
import { useParams, useHistory } from 'react-router-dom'
import axios from 'axios'
import jsPDF from 'jspdf'
import QRCode from 'qrcode'
import { useDispatch, useSelector } from 'react-redux'
import { elmecLogo, englishFlag, italyFlag } from '../../assets'
import { translations } from '../../utils/constants'
import { setDefaultLanguage } from '../../Redux/actions/defaultLanguageAction'

const ParkingQrDetailsPage = () => {
    const { qrToken } = useParams()
    const history = useHistory()
    const dispatch = useDispatch()
    const language = useSelector((state) => state.defaultLanguageReducer?.language)
    const [loading, setLoading] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [session, setSession] = useState(null)

    useEffect(() => {
        console.log('QR Token from URL:', qrToken)
        if (qrToken) {
            fetchParkingSessionByQr()
        }
    }, [qrToken])

    const handleLanguageChange = (value) => {
        dispatch(setDefaultLanguage(value))
    }
    const fetchParkingSessionByQr = async () => {
        try {
            setLoading(true)

            const res = await axios.get(`${GET?.GET_PARKING_SESSION_BY_QR}/${qrToken}`)
            console.log('Parking session response:', res)

            const response = res?.data
            console.log('Parking session response data:', response)

            if (!response?.success) {
                setSession(null)
                message.error(response?.message || 'Parking session not found')
                return
            }

            setSession(response?.data)
        } catch (error) {
            console.error(error)
            message.error('Failed to load parking QR details')
        } finally {
            setLoading(false)
        }
    }

    const getPaymentColor = status => {
        if (status === 'paid') return 'green'
        if (status === 'pending') return 'orange'
        if (status === 'failed') return 'red'
        return 'default'
    }

    const getSessionColor = status => {
        if (status === 'entered') return 'blue'
        if (status === 'paid') return 'green'
        if (status === 'exited') return 'purple'
        if (status === 'blocked') return 'red'
        return 'default'
    }

    const formatDateTime = value => {
        if (!value) return '-'
        return new Date(value).toLocaleString()
    }

    const getPlateNumber = () => {
        return (
            session?.corrected_plate_number ||
            session?.vehicle?.plate_number ||
            session?.plate_number ||
            session?.vehicle_plate ||
            '-'
        )
    }

    const getPlateImageUrl = () => {
        return session?.image_url || session?.picture_gs_url || session?.picture_url || null
    }

    const getImageBase64 = async url => {
        try {
            if (!url) return null

            const response = await fetch(url, {
                mode: 'cors'
            })

            const blob = await response.blob()

            return await new Promise((resolve, reject) => {
                const reader = new FileReader()

                reader.onloadend = () => resolve(reader.result)
                reader.onerror = reject

                reader.readAsDataURL(blob)
            })
        } catch (error) {
            console.error('getImageBase64 error:', error)
            return null
        }
    }

    const getImageFormatFromBase64 = base64 => {
        if (!base64) return 'JPEG'
        if (base64.includes('image/png')) return 'PNG'
        if (base64.includes('image/webp')) return 'WEBP'
        return 'JPEG'
    }

    const downloadParkingSlipPdf = async () => {
        try {
            if (!session) {
                message.error('Dati della sessione di parcheggio non trovati')
                return
            }

            setPdfLoading(true)

            const plateNumber = getPlateNumber()

            const qrValue =
                session?.qr_url ||
                `${window.location.origin}/parking/qr/${session?.qr_token || qrToken}`

            const qrImage = await QRCode.toDataURL(qrValue, {
                width: 360,
                margin: 1
            })

            // 80mm thermal slip size
            const slipWidth = 80
            const slipHeight = 150

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: [slipWidth, slipHeight]
            })

            const pageWidth = doc.internal.pageSize.getWidth()

            let y = 8

            const centerText = (text, yPos, fontSize = 10, fontStyle = 'normal') => {
                doc.setFont('helvetica', fontStyle)
                doc.setFontSize(fontSize)
                doc.text(String(text || '-'), pageWidth / 2, yPos, { align: 'center' })
            }

            const line = (yPos) => {
                doc.setDrawColor(210, 210, 210)
                doc.line(6, yPos, pageWidth - 6, yPos)
            }

            const translateSessionStatus = (status) => {
                const value = String(status || '').toLowerCase()

                const statuses = {
                    entered: 'ENTRATO',
                    active: 'ATTIVO',
                    pending_payment: 'IN ATTESA DI PAGAMENTO',
                    paid: 'PAGATO',
                    exited: 'USCITO',
                    blocked: 'BLOCCATO',
                    cancelled: 'ANNULLATO'
                }

                return statuses[value] || String(status || '-').toUpperCase()
            }

            const translatePaymentStatus = (status) => {
                const value = String(status || '').toLowerCase()

                const statuses = {
                    unpaid: 'NON PAGATO',
                    pending: 'IN ATTESA',
                    paid: 'PAGATO',
                    failed: 'FALLITO',
                    refunded: 'RIMBORSATO'
                }

                return statuses[value] || String(status || 'unpaid').toUpperCase()
            }

            // Header
            doc.setTextColor(20, 20, 20)
            centerText('Biglietto Parcheggio', y, 14, 'bold')

            y += 7
            centerText('Ricevuta di Ingresso', y, 9, 'normal')

            y += 5
            line(y)

            // Plate number
            y += 10
            doc.setTextColor(110, 110, 110)
            centerText('Numero Targa', y, 8, 'normal')

            y += 10
            doc.setTextColor(20, 20, 20)
            centerText(String(plateNumber || '-').toUpperCase(), y, 22, 'bold')

            // Session code
            y += 9
            doc.setTextColor(110, 110, 110)
            centerText('Codice Sessione', y, 8, 'normal')

            y += 6
            doc.setTextColor(20, 20, 20)
            centerText(session?.session_code || '-', y, 9, 'bold')

            y += 6
            line(y)

            // Entry details
            y += 9

            doc.setFont('helvetica', 'normal')
            doc.setFontSize(8)
            doc.setTextColor(110, 110, 110)
            doc.text('Ora Ingresso', 8, y)

            doc.setFont('helvetica', 'bold')
            doc.setFontSize(8)
            doc.setTextColor(20, 20, 20)
            doc.text(formatDateTime(session?.entry_time) || '-', pageWidth - 8, y, {
                align: 'right'
            })

            y += 7

            doc.setFont('helvetica', 'normal')
            doc.setFontSize(8)
            doc.setTextColor(110, 110, 110)
            doc.text('Stato', 8, y)

            doc.setFont('helvetica', 'bold')
            doc.setFontSize(7)
            doc.setTextColor(20, 20, 20)

            const statusText = `${translateSessionStatus(session?.status)} / ${translatePaymentStatus(session?.payment_status)}`
            const statusLines = doc.splitTextToSize(statusText, 38)

            doc.text(statusLines, pageWidth - 8, y, {
                align: 'right'
            })

            y += statusLines.length > 1 ? 10 : 7

            doc.setFont('helvetica', 'normal')
            doc.setFontSize(8)
            doc.setTextColor(110, 110, 110)
            doc.text('Corsia N.', 8, y)

            doc.setFont('helvetica', 'bold')
            doc.setFontSize(8)
            doc.setTextColor(20, 20, 20)
            doc.text(String(session?.lane_no || '-'), pageWidth - 8, y, {
                align: 'right'
            })

            y += 7
            line(y)

            // QR Code
            y += 8
            centerText('Scansiona il QR alla TVM per pagare', y, 8, 'bold')

            const qrSize = 46
            const qrX = (pageWidth - qrSize) / 2

            y += 4
            doc.addImage(qrImage, 'PNG', qrX, y, qrSize, qrSize)

            y += qrSize + 7

            doc.setTextColor(110, 110, 110)
            centerText('Conservare il biglietto fino all uscita', y, 8, 'normal')

            y += 6
            line(y)

            // Footer
            y += 7
            doc.setFontSize(7)
            doc.setFont('helvetica', 'normal')
            doc.setTextColor(150, 150, 150)
            doc.text('Generato dal sistema Nucleo Parking', pageWidth / 2, y, {
                align: 'center'
            })

            const fileName = `biglietto-parcheggio-${session?.session_code || plateNumber || 'ticket'}.pdf`
            doc.save(fileName)

            message.success('Biglietto parcheggio scaricato con successo')
        }
        catch (error) {
            console.error('downloadParkingSlipPdf error:', error)
            message.error('Errore durante il download del biglietto')
        }
        finally {
            setPdfLoading(false)
        }
    }

    // const downloadParkingSlipPdf = async () => {
    //     try {
    //         if (!session) {
    //             message.error('Parking session data not found')
    //             return
    //         }

    //         setPdfLoading(true)

    //         const plateNumber = getPlateNumber()

    //         const qrValue =
    //             session?.qr_url ||
    //             `${window.location.origin}/parking/qr/${session?.qr_token || qrToken}`

    //         const qrImage = await QRCode.toDataURL(qrValue, {
    //             width: 360,
    //             margin: 1
    //         })

    //         // 80mm thermal slip size
    //         const slipWidth = 80
    //         const slipHeight = 150

    //         const doc = new jsPDF({
    //             orientation: 'portrait',
    //             unit: 'mm',
    //             format: [slipWidth, slipHeight]
    //         })

    //         const pageWidth = doc.internal.pageSize.getWidth()

    //         let y = 8

    //         const centerText = (text, yPos, fontSize = 10, fontStyle = 'normal') => {
    //             doc.setFont('helvetica', fontStyle)
    //             doc.setFontSize(fontSize)
    //             doc.text(String(text || '-'), pageWidth / 2, yPos, { align: 'center' })
    //         }

    //         const line = (yPos) => {
    //             doc.setDrawColor(210, 210, 210)
    //             doc.line(6, yPos, pageWidth - 6, yPos)
    //         }

    //         // Header
    //         doc.setTextColor(20, 20, 20)
    //         centerText('Parking Ticket', y, 14, 'bold')

    //         y += 7
    //         centerText('Entry Slip', y, 9, 'normal')

    //         y += 5
    //         line(y)

    //         // Plate number
    //         y += 10
    //         doc.setTextColor(110, 110, 110)
    //         centerText('Plate Number', y, 8, 'normal')

    //         y += 10
    //         doc.setTextColor(20, 20, 20)
    //         centerText(String(plateNumber || '-').toUpperCase(), y, 22, 'bold')

    //         // Session code
    //         y += 9
    //         doc.setTextColor(110, 110, 110)
    //         centerText('Session Code', y, 8, 'normal')

    //         y += 6
    //         doc.setTextColor(20, 20, 20)
    //         centerText(session?.session_code || '-', y, 9, 'bold')

    //         y += 6
    //         line(y)

    //         // Entry details
    //         y += 9

    //         doc.setFont('helvetica', 'normal')
    //         doc.setFontSize(8)
    //         doc.setTextColor(110, 110, 110)
    //         doc.text('Entry Time', 8, y)

    //         doc.setFont('helvetica', 'bold')
    //         doc.setFontSize(8)
    //         doc.setTextColor(20, 20, 20)
    //         doc.text(formatDateTime(session?.entry_time) || '-', pageWidth - 8, y, {
    //             align: 'right'
    //         })

    //         y += 7

    //         doc.setFont('helvetica', 'normal')
    //         doc.setFontSize(8)
    //         doc.setTextColor(110, 110, 110)
    //         doc.text('Status', 8, y)

    //         doc.setFont('helvetica', 'bold')
    //         doc.setFontSize(8)
    //         doc.setTextColor(20, 20, 20)
    //         doc.text(
    //             `${(session?.status || '-').toUpperCase()} / ${(session?.payment_status || 'unpaid').toUpperCase()}`,
    //             pageWidth - 8,
    //             y,
    //             { align: 'right' }
    //         )

    //         y += 7

    //         doc.setFont('helvetica', 'normal')
    //         doc.setFontSize(8)
    //         doc.setTextColor(110, 110, 110)
    //         doc.text('Lane No', 8, y)

    //         doc.setFont('helvetica', 'bold')
    //         doc.setFontSize(8)
    //         doc.setTextColor(20, 20, 20)
    //         doc.text(String(session?.lane_no || '-'), pageWidth - 8, y, {
    //             align: 'right'
    //         })

    //         y += 7
    //         line(y)

    //         // QR Code
    //         y += 8
    //         centerText('Scan QR at TVM for payment', y, 8, 'bold')

    //         const qrSize = 46
    //         const qrX = (pageWidth - qrSize) / 2

    //         y += 4
    //         doc.addImage(qrImage, 'PNG', qrX, y, qrSize, qrSize)

    //         y += qrSize + 7

    //         doc.setTextColor(110, 110, 110)
    //         centerText('Keep this ticket until exit', y, 8, 'normal')

    //         y += 6
    //         line(y)

    //         // Footer
    //         y += 7
    //         doc.setFontSize(7)
    //         doc.setFont('helvetica', 'normal')
    //         doc.setTextColor(150, 150, 150)
    //         doc.text('Powered by Nucleo Parking System', pageWidth / 2, y, {
    //             align: 'center'
    //         })

    //         const fileName = `parking-slip-${session?.session_code || plateNumber || 'ticket'}.pdf`
    //         doc.save(fileName)

    //         message.success('Parking slip downloaded successfully')
    //     }
    //     catch (error) {
    //         console.error('downloadParkingSlipPdf error:', error)
    //         message.error('Failed to download parking slip')
    //     }
    //     finally {
    //         setPdfLoading(false)
    //     }
    // }

    // const downloadParkingSlipPdf = async () => {
    //     try {
    //         if (!session) {
    //             message.error('Parking session data not found')
    //             return
    //         }

    //         setPdfLoading(true)

    //         const plateNumber = getPlateNumber()
    //         const plateImageUrl = getPlateImageUrl()

    //         const qrValue =
    //             session?.qr_url ||
    //             `${window.location.origin}/parking/qr/${session?.qr_token || qrToken}`

    //         const qrImage = await QRCode.toDataURL(qrValue, {
    //             width: 260,
    //             margin: 1
    //         })

    //         const plateImageBase64 = await getImageBase64(plateImageUrl)

    //         const doc = new jsPDF({
    //             orientation: 'portrait',
    //             unit: 'mm',
    //             format: 'a4'
    //         })

    //         const pageWidth = doc.internal.pageSize.getWidth()
    //         const pageHeight = doc.internal.pageSize.getHeight()

    //         // Header
    //         doc.setFillColor(237, 28, 36)
    //         doc.rect(0, 0, pageWidth, 26, 'F')

    //         doc.setTextColor(255, 255, 255)
    //         doc.setFontSize(18)
    //         doc.setFont('helvetica', 'bold')
    //         doc.text('Parking Ticket Slip', 14, 17)

    //         // Main card
    //         doc.setDrawColor(230, 230, 230)
    //         doc.setFillColor(255, 255, 255)
    //         doc.roundedRect(12, 36, pageWidth - 24, 220, 4, 4, 'FD')

    //         // Title
    //         doc.setTextColor(20, 20, 20)
    //         doc.setFontSize(16)
    //         doc.setFont('helvetica', 'bold')
    //         doc.text('Parking Ticket', 18, 50)

    //         doc.setFontSize(10)
    //         doc.setFont('helvetica', 'normal')
    //         doc.setTextColor(110, 110, 110)
    //         doc.text(`Session Code: ${session?.session_code || '-'}`, 18, 58)

    //         // Plate Number
    //         doc.setTextColor(110, 110, 110)
    //         doc.setFontSize(10)
    //         doc.text('Plate Number', 18, 76)

    //         doc.setTextColor(20, 20, 20)
    //         doc.setFontSize(30)
    //         doc.setFont('helvetica', 'bold')
    //         doc.text(String(plateNumber || '-'), 18, 90)

    //         // Status
    //         doc.setFontSize(10)
    //         doc.setTextColor(110, 110, 110)
    //         doc.setFont('helvetica', 'normal')
    //         doc.text('Session Status', 18, 108)
    //         doc.text('Payment Status', 78, 108)

    //         doc.setFontSize(11)
    //         doc.setTextColor(20, 20, 20)
    //         doc.setFont('helvetica', 'bold')
    //         doc.text((session?.status || '-').toUpperCase(), 18, 116)
    //         doc.text((session?.payment_status || 'unpaid').toUpperCase(), 78, 116)

    //         // QR box
    //         doc.setDrawColor(235, 235, 235)
    //         doc.roundedRect(124, 48, 62, 76, 3, 3, 'S')
    //         doc.addImage(qrImage, 'PNG', 134, 54, 42, 42)

    //         doc.setTextColor(110, 110, 110)
    //         doc.setFontSize(8)
    //         doc.setFont('helvetica', 'normal')
    //         doc.text('Scan QR for session details', 132, 103)

    //         const tokenText = String(session?.qr_token || qrToken || '-')
    //         const tokenLines = doc.splitTextToSize(tokenText, 52)
    //         doc.text(tokenLines, 129, 113)

    //         // Plate picture
    //         doc.setTextColor(20, 20, 20)
    //         doc.setFontSize(12)
    //         doc.setFont('helvetica', 'bold')
    //         doc.text('Plate Picture', 124, 142)

    //         if (plateImageBase64) {
    //             const imageFormat = getImageFormatFromBase64(plateImageBase64)
    //             doc.addImage(plateImageBase64, imageFormat, 124, 149, 62, 42)
    //         }
    //         else {
    //             doc.setDrawColor(210, 210, 210)
    //             doc.roundedRect(124, 149, 62, 42, 3, 3, 'S')
    //             doc.setTextColor(150, 150, 150)
    //             doc.setFontSize(9)
    //             doc.setFont('helvetica', 'normal')
    //             doc.text('No image available', 140, 171)
    //         }

    //         // Details two-column layout
    //         const details = [
    //             ['Entry Time', formatDateTime(session?.entry_time)],
    //             ['Payment Time', formatDateTime(session?.payment_time || session?.paid_at)],
    //             ['Exit Time', formatDateTime(session?.exit_time)],
    //             ['Exit Allowed Until', formatDateTime(session?.exit_allowed_until)],
    //             [
    //                 'Payment Amount',
    //                 session?.payment_amount
    //                     ? `${session?.payment_amount} ${session?.currency || 'EUR'}`
    //                     : '-'
    //             ],
    //             ['Lane No', session?.lane_no || '-'],
    //             ['Device ID', session?.entry_device_id || '-'],
    //             ['Camera ID', session?.entry_camera_id || '-'],
    //             ['Channel', String(session?.entry_channel ?? '-')]
    //         ]

    //         const leftX = 18
    //         const rightX = 72
    //         const startY = 136
    //         const colWidth = 45
    //         const rowHeight = 25

    //         details.forEach(([label, value], index) => {
    //             const isRightCol = index % 2 === 1
    //             const x = isRightCol ? rightX : leftX
    //             const y = startY + Math.floor(index / 2) * rowHeight

    //             doc.setFontSize(8)
    //             doc.setTextColor(110, 110, 110)
    //             doc.setFont('helvetica', 'normal')
    //             doc.text(label, x, y)

    //             doc.setFontSize(9)
    //             doc.setTextColor(20, 20, 20)
    //             doc.setFont('helvetica', 'bold')

    //             const valueText = String(value || '-')
    //             const lines = doc.splitTextToSize(valueText, colWidth)
    //             doc.text(lines.slice(0, 3), x, y + 6)
    //         })

    //         // Footer separator
    //         doc.setDrawColor(235, 235, 235)
    //         doc.line(18, 268, pageWidth - 18, 268)

    //         // Footer
    //         doc.setTextColor(110, 110, 110)
    //         doc.setFontSize(9)
    //         doc.setFont('helvetica', 'normal')
    //         doc.text(
    //             'Please keep this slip until your parking session is completed.',
    //             18,
    //             278
    //         )

    //         doc.setTextColor(150, 150, 150)
    //         doc.setFontSize(8)
    //         doc.text(`Generated at: ${new Date().toLocaleString()}`, 18, 286)

    //         const fileName = `parking-slip-${session?.session_code || plateNumber || 'ticket'}.pdf`
    //         doc.save(fileName)

    //         message.success('Parking slip downloaded successfully')
    //     }
    //     catch (error) {
    //         console.error('downloadParkingSlipPdf error:', error)
    //         message.error('Failed to download parking slip')
    //     }
    //     finally {
    //         setPdfLoading(false)
    //     }
    // }

    if (loading) {
        return (
            <div style={styles.loaderWrapper}>
                <Spin size="large" />
            </div>
        )
    }

    if (!session) {
        return (
            <>
                <div className='header-main-div'>
                    <div className='header-inner-div'>
                        <div className='web-menu-div'>
                            <div>
                                <div className='logo-container'>
                                    <div className='logo-main-div'>
                                        <img src={elmecLogo} alt='Default Logo' className='sidelogo' />
                                        {/* <p className='org-name'>{organizationDetails?.name || ''}</p> */}
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div className='header-inner2-div'>
                            <div className='header-icons'>
                                <div className="header-language-div">
                                    <Select
                                        defaultValue={language}
                                        style={{
                                            width: 30,
                                            marginRight: '11px',
                                        }}
                                        onChange={handleLanguageChange}
                                        suffixIcon={null}
                                        dropdownMatchSelectWidth={false}
                                        dropdownStyle={{
                                            minWidth: 5,
                                            padding: 0
                                        }}
                                        className="flag-select"
                                    >
                                        <Option value="it">
                                            <img
                                                src={italyFlag}
                                                alt="Italian"
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </Option>
                                        <Option value="en">
                                            <img
                                                src={englishFlag}
                                                alt="English"
                                                style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </Option>
                                    </Select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div style={styles.page}>
                    <div style={styles.header}>
                        <h1 style={styles.title}>{translations[language]?.parkingQRDetails}</h1>
                    </div>

                    <Card style={styles.card} bodyStyle={styles.emptyBody}>
                        <Empty
                            image={
                                <div style={styles.emptyIconWrapper}>
                                    <QrcodeOutlined style={styles.emptyIcon} />
                                </div>
                            }
                            description={
                                <div>
                                    <h3 style={styles.emptyTitle}>
                                        {translations[language]?.invalidQr}
                                    </h3>
                                    <p style={styles.emptyText}>
                                        {translations[language]?.noSessionFound}
                                    </p>
                                </div>
                            }
                        />
                    </Card>
                </div>
            </>
        )
    }


    return (
        <>
            <div className='header-main-div'>
                <div className='header-inner-div'>
                    <div className='web-menu-div'>
                        <div>
                            <div className='logo-container'>
                                <div className='logo-main-div'>
                                    <img src={elmecLogo} alt='Default Logo' className='sidelogo' />
                                    {/* <p className='org-name'>{organizationDetails?.name || ''}</p> */}
                                </div>
                            </div>

                        </div>
                    </div>
                    <div className='header-inner2-div'>
                        <div className='header-icons'>
                            <div className="header-language-div">
                                <Select
                                    defaultValue={language}
                                    style={{
                                        width: 30,
                                        marginRight: '11px',
                                    }}
                                    onChange={handleLanguageChange}
                                    suffixIcon={null}
                                    dropdownMatchSelectWidth={false}
                                    dropdownStyle={{
                                        minWidth: 5,
                                        padding: 0
                                    }}
                                    className="flag-select"
                                >
                                    <Option value="it">
                                        <img
                                            src={italyFlag}
                                            alt="Italian"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Option>
                                    <Option value="en">
                                        <img
                                            src={englishFlag}
                                            alt="English"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Option>
                                </Select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={styles.page}>

                <div style={styles.header}>
                    <div>
                        <h1 style={styles.title}>{translations[language]?.parkingQRDetails}</h1>
                        <p style={styles.subtitle}>
                            {translations[language]?.viewParkingSessionDetails}
                        </p>
                    </div>

                    <div style={styles.headerActions}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={pdfLoading}
                            onClick={downloadParkingSlipPdf}
                            style={styles.downloadButton}
                        >
                            {translations[language]?.downloadSlip}
                        </Button>

                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => history.goBack()}
                            style={styles.backButton}
                        >
                            {translations[language]?.goBack}
                        </Button>
                    </div>
                </div>

                <div style={styles.grid}>
                    <Card style={styles.mainCard}>
                        <div style={styles.ticketHeader}>
                            <div style={styles.qrIconBox}>
                                <QrcodeOutlined style={styles.qrIcon} />
                            </div>

                            <div>
                                <h2 style={styles.ticketTitle}>
                                    {translations[language]?.parkingTicket}
                                </h2>
                                <p style={styles.ticketSubTitle}>
                                    {translations[language]?.sessionCode}: {session?.session_code || '-'}
                                </p>
                            </div>
                        </div>

                        <div style={styles.plateBox}>
                            <div>
                                <p style={styles.label}>
                                    {translations[language]?.plateNumber}
                                </p>
                                <h1 style={styles.plateText}>{getPlateNumber()}</h1>
                            </div>

                            {session?.is_plate_corrected && (
                                <Tag color="orange">
                                    {translations[language]?.correctedPlate}
                                </Tag>
                            )}
                        </div>

                        <div style={styles.statusRow}>
                            <div>
                                <p style={styles.label}>
                                    {translations[language]?.sessionStatus}
                                </p>
                                <Tag color={getSessionColor(session?.status)}>
                                    {(session?.status || '-').toUpperCase()}
                                </Tag>
                            </div>

                            <div>
                                <p style={styles.label}>
                                    {translations[language]?.paymentStatus}
                                </p>
                                <Tag color={getPaymentColor(session?.payment_status)}>
                                    {(session?.payment_status || 'unpaid').toUpperCase()}
                                </Tag>
                            </div>
                        </div>

                        <div style={styles.infoGrid}>
                            <InfoItem
                                icon={<ClockCircleOutlined />}
                                label={translations[language]?.entryTime}
                                value={formatDateTime(session?.entry_time)}
                            />

                            <InfoItem
                                icon={<CreditCardOutlined />}
                                label={translations[language]?.paymentTime}
                                value={formatDateTime(session?.payment_time || session?.paid_at)}
                            />

                            <InfoItem
                                icon={<ClockCircleOutlined />}
                                label={translations[language]?.exitTime}
                                value={formatDateTime(session?.exit_time)}
                            />

                            <InfoItem
                                icon={<ClockCircleOutlined />}
                                label={translations[language]?.exitAllowedUntil}
                                value={formatDateTime(session?.exit_allowed_until)}
                            />

                            <InfoItem
                                icon={<FileTextOutlined />}
                                label={translations[language]?.paymentAmount}
                                value={
                                    session?.payment_amount
                                        ? `${session?.payment_amount} ${session?.currency || 'EUR'}`
                                        : '-'
                                }
                            />

                            <InfoItem
                                icon={<CarOutlined />}
                                label={translations[language]?.laneNo}
                                value={session?.lane_no || '-'}
                            />
                        </div>
                    </Card>

                    <Card style={styles.sideCard}>
                        <h3 style={styles.sideTitle}>
                            {translations[language]?.platePicture}
                        </h3>

                        {getPlateImageUrl() ? (
                            <div style={styles.imageWrapper}>
                                <Image
                                    src={getPlateImageUrl()}
                                    alt={translations[language]?.plateAlt || "Plate"}
                                    style={styles.plateImage}
                                    fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIyMCIgdmlld0JveD0iMCAwIDQwMCAyMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSIyMjAiIGZpbGw9IiNmM2Y0ZjYiLz48dGV4dCB4PSIyMDAiIHk9IjExMCIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzk5YTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2Ugbm90IGF2YWlsYWJsZTwvdGV4dD48L3N2Zz4="
                                />
                            </div>
                        ) : (
                            <div style={styles.noImageBox}>
                                <CarOutlined style={styles.noImageIcon} />
                                <p style={styles.emptyText}>
                                    {translations[language]?.noPlatePicture}
                                </p>
                            </div>
                        )}

                        <div style={styles.sideInfo}>
                            <SmallInfo
                                label={translations[language]?.qrToken}
                                value={session?.qr_token || qrToken}
                            />
                            <SmallInfo
                                label={translations[language]?.deviceId}
                                value={session?.entry_device_id || '-'}
                            />
                            <SmallInfo
                                label={translations[language]?.cameraId}
                                value={session?.entry_camera_id || '-'}
                            />
                            <SmallInfo
                                label={translations[language]?.channel}
                                value={session?.entry_channel ?? '-'}
                            />
                        </div>
                    </Card>
                </div>
            </div>
        </>

    )
}

const InfoItem = ({ icon, label, value }) => {
    return (
        <div style={styles.infoItem}>
            <div style={styles.infoIcon}>{icon}</div>
            <div>
                <p style={styles.label}>{label}</p>
                <p style={styles.value}>{value}</p>
            </div>
        </div>
    )
}

const SmallInfo = ({ label, value }) => {
    return (
        <div style={styles.smallInfo}>
            <p style={styles.smallLabel}>{label}</p>
            <p style={styles.smallValue}>{value}</p>
        </div>
    )
}

const styles = {
    page: {
        padding: '24px',
        background: '#f5f6f8',
        minHeight: '100vh'
    },

    loaderWrapper: {
        minHeight: '100vh',
        background: '#f5f6f8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '28px',
        gap: '16px',
        marginTop: '38px',
    },

    headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },

    title: {
        fontSize: '32px',
        fontWeight: 700,
        margin: 0,
        color: '#111'
    },

    subtitle: {
        margin: '8px 0 0',
        color: '#6b7280',
        fontSize: '14px'
    },

    downloadButton: {
        height: '40px',
        borderRadius: '8px',
        fontWeight: 600,
        background: '#ed1c24',
        borderColor: '#ed1c24'
    },

    backButton: {
        height: '40px',
        borderRadius: '8px',
        fontWeight: 600
    },

    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 420px',
        gap: '22px'
    },

    card: {
        borderRadius: '12px',
        border: 'none',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
    },

    mainCard: {
        borderRadius: '12px',
        border: 'none',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
    },

    sideCard: {
        borderRadius: '12px',
        border: 'none',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
    },

    ticketHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        paddingBottom: '22px',
        borderBottom: '1px solid #eef0f3'
    },

    qrIconBox: {
        width: '58px',
        height: '58px',
        borderRadius: '14px',
        background: '#fff1f2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    qrIcon: {
        fontSize: '30px',
        color: '#ed1c24'
    },

    ticketTitle: {
        margin: 0,
        fontSize: '24px',
        fontWeight: 700,
        color: '#111'
    },

    ticketSubTitle: {
        margin: '6px 0 0',
        color: '#6b7280',
        fontSize: '14px'
    },

    plateBox: {
        marginTop: '24px',
        padding: '22px',
        background: '#fafafa',
        borderRadius: '12px',
        border: '1px solid #eef0f3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    label: {
        margin: '0 0 6px',
        color: '#6b7280',
        fontSize: '13px'
    },

    plateText: {
        margin: 0,
        fontSize: '42px',
        fontWeight: 800,
        letterSpacing: '2px',
        color: '#111'
    },

    statusRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '18px',
        marginTop: '22px'
    },

    infoGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginTop: '24px'
    },

    infoItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '16px',
        border: '1px solid #eef0f3',
        borderRadius: '12px',
        background: '#fff'
    },

    infoIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        background: '#fff1f2',
        color: '#ed1c24',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px'
    },

    value: {
        margin: 0,
        color: '#111',
        fontSize: '15px',
        fontWeight: 600
    },

    sideTitle: {
        margin: '0 0 16px',
        fontSize: '18px',
        fontWeight: 700,
        color: '#111'
    },

    imageWrapper: {
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        background: '#f3f4f6',
        border: '1px solid #eef0f3'
    },

    plateImage: {
        width: '100%',
        maxHeight: '260px',
        objectFit: 'cover',
        display: 'block'
    },

    noImageBox: {
        height: '220px',
        borderRadius: '12px',
        border: '1px dashed #d1d5db',
        background: '#fafafa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
    },

    noImageIcon: {
        fontSize: '42px',
        color: '#9ca3af',
        marginBottom: '10px'
    },

    sideInfo: {
        marginTop: '18px'
    },

    smallInfo: {
        padding: '12px 0',
        borderBottom: '1px solid #eef0f3'
    },

    smallLabel: {
        margin: '0 0 4px',
        color: '#6b7280',
        fontSize: '12px'
    },

    smallValue: {
        margin: 0,
        color: '#111',
        fontSize: '13px',
        fontWeight: 600,
        wordBreak: 'break-all'
    },

    emptyBody: {
        minHeight: '290px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    emptyIconWrapper: {
        width: '58px',
        height: '58px',
        borderRadius: '50%',
        border: '4px solid #9ca3af',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 12px'
    },

    emptyIcon: {
        fontSize: '28px',
        color: '#9ca3af'
    },

    emptyTitle: {
        fontSize: '17px',
        color: '#111',
        margin: '8px 0'
    },

    emptyText: {
        color: '#6b7280',
        fontSize: '14px',
        marginBottom: 0
    }
}

export default ParkingQrDetailsPage